// US-UI-MOBILE-LAYOUT -- mobile-only master list rendered in place of
// HomeScreen for guardian sessions when isMobileLayout() returns true.
//
// Master/detail design:
//   * This screen is the MASTER -- a compact tappable list of the
//     guardian's own info row + each linked child.
//   * Tapping a row navigates to the corresponding DETAIL screen
//     (ChildDetailScreen for children; the guardian's own row opens
//     Settings since there's no per-self bucket detail to show in
//     a guardian session).
//
// Each child row shows:
//   - Name
//   - Total balance across all of that child's buckets
//   - Pending-approvals badge count for that child
//
// The header carries the same Approvals bell + Settings button as
// the desktop HomeScreen so the mobile experience preserves access
// to the same global actions.

import {
  type AccountListEntry,
  type BucketEntry,
  bucketsList,
  familyChallengeCurrent,
  familyChallengeEnd,
  familyChallengeStart,
  pendingChoresList,
  pendingDepositsList,
  pendingTransfersList,
  pendingWithdrawalsList,
  routinePendingApprovals,
} from '@chorepop/core/client';
import {
  type Component,
  For,
  Show,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js';
import { LoginSecurityAlertDialog } from '../components/LoginSecurityAlertDialog';
import { TransientBanner } from '../components/TransientBanner';
import { FamilyChallengeCard } from './FamilyChallengeCard';
import { PendingApprovalsScreen } from './PendingApprovalsScreen';

type Props = {
  account: AccountListEntry;
  sessionId: string;
  flashMessage?: string | undefined;
  onFlashDismiss?: (() => void) | undefined;
  onAddChild: () => void;
  onAddGuardian: () => void;
  onOpenSettings: () => void;
  onViewPendingApprovals: () => void;
  /** Tap a child row -> navigate to that child's detail screen. */
  onSelectChild: (childAccountId: number, childDisplayName: string) => void;
};

type PendingPreview = {
  id: string;
  label: string;
  childName: string;
  amountMinorUnits: number;
  kind: 'chore' | 'deposit' | 'withdrawal' | 'transfer' | 'routine';
  requestedAtUtc: string;
};

function pendingPreviewAge(requestedAtUtc: string): string {
  const elapsedMinutes = Math.max(
    0,
    Math.floor((Date.now() - Date.parse(requestedAtUtc)) / 60_000),
  );
  if (elapsedMinutes < 1) return 'just now';
  if (elapsedMinutes < 60) return `${elapsedMinutes}m ago`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours}h ago`;
  return `${Math.floor(elapsedHours / 24)}d ago`;
}

export const MobileGuardianHomeScreen: Component<Props> = (props) => {
  const [bucketsResource] = createResource(async () => {
    const result = await bucketsList({ sessionId: props.sessionId });
    if (!result.ok) return [];
    return result.value;
  });

  const [pendingResource, { refetch: refetchPending }] = createResource(async () => {
    const [deposits, withdrawals, chores, transfers, routines] = await Promise.all([
      pendingDepositsList({ sessionId: props.sessionId }),
      pendingWithdrawalsList({ sessionId: props.sessionId }),
      pendingChoresList({ sessionId: props.sessionId }),
      pendingTransfersList({ sessionId: props.sessionId }),
      routinePendingApprovals(props.sessionId),
    ]);
    const dArr = deposits.ok ? deposits.value : [];
    const wArr = withdrawals.ok ? withdrawals.value : [];
    const cArr = chores.ok ? chores.value : [];
    const tArr = transfers.ok ? transfers.value : [];
    const rArr = routines.ok ? routines.value : [];
    const perChild = new Map<number, number>();
    for (const d of dArr) {
      perChild.set(d.childAccountId, (perChild.get(d.childAccountId) ?? 0) + 1);
    }
    for (const w of wArr) {
      perChild.set(w.childAccountId, (perChild.get(w.childAccountId) ?? 0) + 1);
    }
    for (const c of cArr) {
      perChild.set(c.childAccountId, (perChild.get(c.childAccountId) ?? 0) + 1);
    }
    for (const t of tArr) {
      perChild.set(t.childAccountId, (perChild.get(t.childAccountId) ?? 0) + 1);
    }
    for (const routine of rArr) {
      if (typeof routine.childAccountId === 'number') {
        perChild.set(routine.childAccountId, (perChild.get(routine.childAccountId) ?? 0) + 1);
      }
    }
    const preview: PendingPreview[] = [
      ...dArr.map((deposit) => ({
        id: `deposit-${deposit.depositId}`,
        label: `Deposit · ${deposit.sourceLabel}`,
        childName: deposit.childDisplayName,
        amountMinorUnits: deposit.amountMinorUnits,
        kind: 'deposit' as const,
        requestedAtUtc: deposit.requestedAtUtc,
      })),
      ...wArr.map((withdrawal) => ({
        id: `withdrawal-${withdrawal.withdrawalId}`,
        label: `Withdrawal · ${withdrawal.bucketName}`,
        childName: withdrawal.childDisplayName,
        amountMinorUnits: -Math.abs(withdrawal.amountMinorUnits),
        kind: 'withdrawal' as const,
        requestedAtUtc: withdrawal.requestedAtUtc,
      })),
      ...cArr.map((chore) => ({
        id: `chore-${chore.choreCompletionId}`,
        label: `Chore · ${chore.choreName}`,
        childName: chore.childDisplayName,
        amountMinorUnits: chore.rewardMinorUnits,
        kind: 'chore' as const,
        requestedAtUtc: chore.claimedAtUtc,
      })),
      ...tArr.map((transfer) => ({
        id: `transfer-${transfer.bucketTransferId}`,
        label: `Move money · ${transfer.sourceBucketName}`,
        childName: transfer.childDisplayName,
        amountMinorUnits: transfer.amountMinorUnits,
        kind: 'transfer' as const,
        requestedAtUtc: transfer.requestedAtUtc,
      })),
      ...rArr.map((routine) => ({
        id: `routine-${routine.instanceId}`,
        label: `Routine · ${routine.taskName}`,
        childName: routine.childDisplayName ?? 'Child',
        amountMinorUnits: routine.rewardMinorUnits,
        kind: 'routine' as const,
        requestedAtUtc: routine.submittedAtUtc ?? routine.scheduledDate,
      })),
    ].sort((a, b) => Date.parse(b.requestedAtUtc) - Date.parse(a.requestedAtUtc));

    return {
      total: dArr.length + wArr.length + cArr.length + tArr.length + rArr.length,
      perChild,
      deposits: dArr.length,
      withdrawals: wArr.length,
      chores: cArr.length + rArr.length,
      transfers: tArr.length,
      preview,
    };
  });

  const [selectingMember, setSelectingMember] = createSignal(false);
  const [familyChallenge, { refetch: refetchFamilyChallenge }] = createResource(
    () => props.sessionId,
    (sessionId) =>
      familyChallengeCurrent(sessionId).then((result) => (result.ok ? result.value : null)),
  );

  const startFamilyChallenge = async (challengeName: string, targetDaysPerChild: number) => {
    const result = await familyChallengeStart({
      sessionId: props.sessionId,
      challengeName,
      targetDaysPerChild,
    });
    if (!result.ok) return result.error.message;
    await refetchFamilyChallenge();
    return null;
  };

  const endFamilyChallenge = async (familyChallengeId: number) => {
    const result = await familyChallengeEnd({
      sessionId: props.sessionId,
      familyChallengeId,
    });
    if (!result.ok) return result.error.message;
    await refetchFamilyChallenge();
    return null;
  };

  onMount(() => {
    const handleSync = () => void refetchFamilyChallenge();
    window.addEventListener('chorepop:sync-applied', handleSync);
    onCleanup(() => window.removeEventListener('chorepop:sync-applied', handleSync));
  });

  const pendingTotal = (): number => pendingResource()?.total ?? 0;
  const pendingFor = (childId: number): number => pendingResource()?.perChild.get(childId) ?? 0;

  const grouped = createMemo<
    Array<{ childId: number; childName: string; buckets: BucketEntry[]; total: number }>
  >(() => {
    const map = new Map<number, BucketEntry[]>();
    for (const b of bucketsResource() ?? []) {
      const arr = map.get(b.childAccountId) ?? [];
      arr.push(b);
      map.set(b.childAccountId, arr);
    }
    return Array.from(map.entries()).map(([childId, buckets]) => ({
      childId,
      childName: buckets[0]?.childDisplayName ?? `Child #${childId}`,
      buckets,
      total: buckets.reduce((s, b) => s + b.balanceMinorUnits, 0),
    }));
  });

  return (
    <main class="mobile-home-screen guardian-overview-screen">
      <header class="guardian-overview-header">
        <div class="guardian-overview-title-group">
          <span class="guardian-overview-account-avatar" aria-hidden="true">
            {props.account.displayAvatar ?? props.account.displayName.slice(0, 1).toUpperCase()}
          </span>
          <div>
            <span class="guardian-overview-welcome">Welcome, {props.account.displayName}</span>
            <h1 class="guardian-overview-title">Family Overview</h1>
          </div>
        </div>
        <div class="mobile-home-header-actions guardian-desktop-header-actions">
          <button
            type="button"
            class="ghost"
            onClick={() => props.onOpenSettings()}
            data-testid="home-open-settings"
          >
            Settings
          </button>
          <button
            type="button"
            class="primary guardian-overview-add"
            onClick={() => setSelectingMember(true)}
            data-testid="mobile-home-add-child"
          >
            Add family member
          </button>
        </div>
      </header>

      <Show when={props.flashMessage}>
        {(message) => (
          <TransientBanner
            class="home-flash"
            message={message()}
            onDismiss={props.onFlashDismiss}
          />
        )}
      </Show>

      <LoginSecurityAlertDialog
        sessionId={props.sessionId}
        guardianAccountId={props.account.accountId}
      />

      <div class="guardian-overview-grid">
        <div class="guardian-overview-side">
          <section class="guardian-mobile-approval-banner" aria-label="Pending approvals">
            <button
              type="button"
              class="guardian-mobile-approval-heading"
              onClick={() => props.onViewPendingApprovals()}
              data-testid="mobile-home-approvals-banner"
            >
              <span class="guardian-mobile-approval-heading-copy">
                <span class="mobile-home-eyebrow">{pendingTotal()} waiting on you</span>
                <strong>Pending approvals</strong>
                <small>Chores, deposits &amp; withdrawals</small>
              </span>
              <span class="guardian-mobile-approval-chevron" aria-hidden="true">
                ›
              </span>
            </button>
            <Show
              when={(pendingResource()?.preview.length ?? 0) > 0}
              fallback={
                <p class="guardian-mobile-approval-empty">Nothing waiting for your approval.</p>
              }
            >
              <For each={pendingResource()?.preview.slice(0, 2) ?? []}>
                {(item) => (
                  <button
                    type="button"
                    class="guardian-mobile-approval-row"
                    onClick={() => props.onViewPendingApprovals()}
                  >
                    <span
                      class={`guardian-mobile-approval-icon is-${item.kind}`}
                      aria-hidden="true"
                    >
                      {item.kind === 'chore' ? '✓' : item.kind === 'withdrawal' ? '−' : '＋'}
                    </span>
                    <span class="guardian-mobile-approval-row-copy">
                      <strong>{item.label}</strong>
                      <small>
                        {item.childName} · {pendingPreviewAge(item.requestedAtUtc)}
                      </small>
                    </span>
                    <span
                      class={`guardian-mobile-approval-amount ${item.amountMinorUnits < 0 ? 'is-negative' : ''}`}
                    >
                      {formatBalance(item.amountMinorUnits, props.account.displayCurrencyCode)}
                    </span>
                  </button>
                )}
              </For>
            </Show>
          </section>

          <section class="guardian-overview-card mobile-home-list-section">
            <span class="mobile-home-eyebrow">Children</span>
            <h2 class="mobile-home-section-heading">Family balances</h2>
            <p class="guardian-card-summary">
              {formatBalance(
                grouped().reduce((sum, child) => sum + child.total, 0),
                props.account.displayCurrencyCode,
              )}{' '}
              · {pendingTotal()} waiting
            </p>
            <Show
              when={grouped().length > 0}
              fallback={
                <p class="mobile-home-empty">
                  No children yet. Add a family member to get started.
                </p>
              }
            >
              <ul class="mobile-home-list">
                <For each={grouped()}>
                  {(c) => (
                    <li>
                      <button
                        type="button"
                        class="mobile-home-row"
                        onClick={() => props.onSelectChild(c.childId, c.childName)}
                        data-testid={`mobile-home-child-${c.childId}`}
                        aria-label={`Manage ${c.childName}`}
                      >
                        <span class="mobile-home-row-avatar" aria-hidden="true">
                          {c.childName.slice(0, 1).toUpperCase()}
                        </span>
                        <span class="mobile-home-row-text">
                          <span class="mobile-home-row-name">{c.childName}</span>
                          <span class="mobile-home-row-balance num">
                            {formatBalance(c.total, props.account.displayCurrencyCode)}
                          </span>
                          <span class="guardian-child-split-bar" aria-label="Bucket split">
                            <For each={c.buckets}>
                              {(bucket) => (
                                <span
                                  style={`--bucket-share: ${Math.max(bucket.allocationBps, 1)}; --bucket-color: ${bucket.displayColor ?? 'var(--brand)'}`}
                                  title={`${bucket.bucketName} ${Math.round(bucket.allocationBps / 100)}%`}
                                />
                              )}
                            </For>
                          </span>
                        </span>
                        <span
                          class="guardian-child-approval-label"
                          data-testid={`mobile-home-row-badge-${c.childId}`}
                        >
                          {pendingFor(c.childId) === 0
                            ? 'No approvals'
                            : `${pendingFor(c.childId)} approval${pendingFor(c.childId) === 1 ? '' : 's'}`}
                        </span>
                        <span class="mobile-home-row-chevron" aria-hidden="true">
                          ›
                        </span>
                      </button>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </section>

          <FamilyChallengeCard
            challenge={familyChallenge()}
            loading={familyChallenge.loading}
            onStart={startFamilyChallenge}
            onEnd={endFamilyChallenge}
          />
        </div>

        <section class="guardian-overview-card guardian-approval-hub">
          <div class="guardian-approval-heading">
            <div>
              <span class="mobile-home-eyebrow">{pendingTotal()} waiting</span>
              <h2 class="mobile-home-section-heading">Pending approvals</h2>
            </div>
            <button
              type="button"
              class="ghost"
              onClick={() => props.onViewPendingApprovals()}
              data-testid="mobile-home-approvals-bell"
              aria-label="Open pending approvals in full screen"
            >
              Approval history
            </button>
          </div>
          <div class="guardian-approval-counts" aria-label="Approval types">
            <span>
              <strong>{pendingTotal()}</strong> All
            </span>
            <span>
              <strong>{pendingResource()?.chores ?? 0}</strong> Chores
            </span>
            <span>
              <strong>{pendingResource()?.deposits ?? 0}</strong> Deposits
            </span>
            <span>
              <strong>{pendingResource()?.withdrawals ?? 0}</strong> Withdrawals
            </span>
          </div>
          <PendingApprovalsScreen
            embedded
            sessionId={props.sessionId}
            currencyCode={props.account.displayCurrencyCode}
            onQueueChanged={() => void refetchPending()}
          />
          <section class="guardian-today-glance" aria-label="Today at a glance">
            <span class="mobile-home-eyebrow">Today at a glance</span>
            <div class="guardian-today-glance-grid">
              <div>
                <strong>{pendingResource()?.chores ?? 0}</strong>
                <span>Chores waiting</span>
              </div>
              <div>
                <strong>{pendingResource()?.deposits ?? 0}</strong>
                <span>Deposits waiting</span>
              </div>
              <div>
                <strong>{pendingResource()?.withdrawals ?? 0}</strong>
                <span>Withdrawals waiting</span>
              </div>
              <div>
                <strong>
                  {formatBalance(
                    grouped().reduce((sum, child) => sum + child.total, 0),
                    props.account.displayCurrencyCode,
                  )}
                </strong>
                <span>Family total</span>
              </div>
            </div>
          </section>
        </section>
      </div>

      <Show when={selectingMember()}>
        <div
          class="confirm-backdrop"
          onClick={(event) => event.target === event.currentTarget && setSelectingMember(false)}
        >
          <section
            class="confirm-dialog guardian-picker-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-family-member-title"
          >
            <span class="mobile-home-eyebrow">Family setup</span>
            <h2 id="add-family-member-title">Who would you like to add?</h2>
            <div class="guardian-picker-options">
              <button
                type="button"
                class="primary"
                onClick={() => {
                  setSelectingMember(false);
                  props.onAddChild();
                }}
              >
                Add a child
              </button>
              <button
                type="button"
                class="ghost"
                onClick={() => {
                  setSelectingMember(false);
                  props.onAddGuardian();
                }}
              >
                Add a parent or guardian
              </button>
              <button type="button" class="ghost" onClick={() => setSelectingMember(false)}>
                Cancel
              </button>
            </div>
          </section>
        </div>
      </Show>
    </main>
  );
};

function formatBalance(minorUnits: number, currencyCode: string): string {
  const major = minorUnits / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(major);
  } catch {
    return `${currencyCode} ${major.toFixed(2)}`;
  }
}
